import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import Component from '@glimmer/component'
import { classesForTheme } from 'teacher-ui-client/components/copy-classes/themes'
import { ThemeKeys, subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import { fn, hash } from '@ember/helper'
import { on } from '@ember/modifier'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import DataOperationsTable from 'teacher-ui-client/components/data-operations/table'
import { SortType } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import type { SortDirection } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import { tracked } from '@glimmer/tracking'
import includes from 'teacher-ui-client/helpers/includes'
import { t } from 'ember-intl'
import {
  buildDataOperationsConfigProxy,
  type DataOperationsConfig,
  type DataOperationsController,
} from 'teacher-ui-client/components/data-operations/data-operations-proxy'
import { type CopyClassesPage } from 'teacher-ui-client/components/page/copy-classes'
import { doAction } from 'teacher-ui-client/modifiers/do-action'
import perform from 'ember-concurrency/helpers/perform'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import type { FromClassRow } from 'teacher-ui-client/components/copy-classes/from-class-option'
import pick from 'teacher-ui-client/helpers/pick'
import { action } from '@ember/object'
import { not, gt } from 'ember-truth-helpers'
import { ColumnSortIcon } from 'teacher-ui-client/components/data-operations/table/column-sort-icon'
import { RowLoading } from 'teacher-ui-client/components/base/row-loading'
import { colspanMax } from 'teacher-ui-client/modifiers/colspan-max'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import config from 'teacher-ui-client/config/environment'
import type { TeacherOptionTypes } from 'teacher-ui-client/components/base/select/teacher/options'
import type Owner from '@ember/owner'

const MAX_STUDENTS_PER_CLASS = config.copyClasses.maxStudentsPerClass

interface Signature {
  Element: HTMLTableElement
  Args: {
    toSubscriptionType: SubscriptionType
    fetchSchoolClasses: CopyClassesPage['fetchSchoolClasses']
  }
}

export class CopyClassesTable extends Component<Signature> implements DataOperationsController {
  @tracked sortCol?: string

  @tracked sortDir?: SortDirection

  @tracked sortCompare?: SortType

  @tracked currentPage?: number

  recordsPerPage = Number.POSITIVE_INFINITY

  @queryParam('subscription-type/copy-classes') fromSubscriptionType!: SubscriptionType

  @queryParam('subscription-type/copy-classes') selectedIds!: string[]

  @queryParam('subscription-type/copy-classes') teacherId?: TeacherOptionTypes | string

  @queryParam('subscription-type/copy-classes') hideEmptyClasses!: boolean

  dataOperationsConfig: DataOperationsConfig<FromClassRow>

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)
    this.dataOperationsConfig = buildDataOperationsConfigProxy(this)
  }

  get data() {
    return this.args.fetchSchoolClasses.lastComplete?.value ?? []
  }

  get themeKey() {
    return subscriptionTypeToThemeKey(this.args.toSubscriptionType)
  }

  get themeClasses() {
    return classesForTheme(this.themeKey)
  }

  get hasLoaded() {
    return Boolean(this.args.fetchSchoolClasses.lastComplete)
  }

  @action
  onChangeName(fromClassRow: FromClassRow, value?: string) {
    fromClassRow.set('name', value)
  }

  <template>
    <DataOperationsTable @dataOperationsConfig={{this.dataOperationsConfig}} as |selectRecords sorting|>
      <table
        class="relative h-fit table-fixed"
        ...attributes
        {{doAction
          (perform
            @fetchSchoolClasses
            (hash
              subscriptionType=this.fromSubscriptionType teacherId=this.teacherId hideEmptyClasses=this.hideEmptyClasses
            )
          )
        }}
      >
        <thead class="{{this.themeClasses.background.light}}">
          <tr class="border-t-dusty-black-100 divide-x border-t border-b border-b-white">
            <th class="w-24">
              <label
                for="all-classes-checkbox"
                class="inline-flex w-full cursor-pointer flex-col items-center gap-2 py-2 text-center font-bold select-none"
              >
                {{t "all"}}
                <input
                  type="checkbox"
                  class="cursor-pointer"
                  id="all-classes-checkbox"
                  name="all-classes-checkbox"
                  value="copy-all-classes"
                  checked={{selectRecords.allSelected}}
                  {{on "click" selectRecords.toggleAll}}
                />
              </label>
            </th>
            <th>
              <button
                type="button"
                class="h-full w-full cursor-pointer px-3 text-left whitespace-nowrap select-none"
                {{on "click" (fn sorting.sort SortType.ByValue "teacherNames")}}
              >
                {{t "components.copyClasses.table.headers.teachers"}}
                <ColumnSortIcon @columnName="teacherNames" @sortCol={{sorting.sortCol}} @sortDir={{sorting.sortDir}} />
              </button>
            </th>
            <th>
              <button
                type="button"
                class="block h-full w-full cursor-pointer px-3 text-left whitespace-nowrap select-none"
                {{on "click" (fn sorting.sort SortType.ByValue "originalName")}}
              >
                {{t "components.copyClasses.table.headers.className"}}
                <ColumnSortIcon @columnName="originalName" @sortCol={{sorting.sortCol}} @sortDir={{sorting.sortDir}} />
              </button>
            </th>
            <th class="w-40">
              <button
                type="button"
                class="h-full w-full cursor-pointer px-3 text-left whitespace-nowrap select-none"
                {{on "click" (fn sorting.sort SortType.ByNumber "studentCount")}}
              >
                {{t "components.copyClasses.table.headers.noOfStudents"}}
                <ColumnSortIcon @columnName="studentCount" @sortCol={{sorting.sortCol}} @sortDir={{sorting.sortDir}} />
              </button>
            </th>
            <th class="px-3">
              {{t "components.copyClasses.table.headers.newClassName"}}
              <span data-test-new-name-tooltip class="float-right print:hidden">
                <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-300 h-5 w-5 cursor-pointer" />
                <Tooltip @text={{t "components.copyClasses.table.newClassNameToolip"}} />
              </span>
            </th>
          </tr>
        </thead>
        <tbody class="relative divide-y">
          {{#each sorting.sortedRecords as |schoolClass|}}
            <tr>
              <td>
                <label class="w-full cursor-pointer px-2 py-4 text-center select-none">
                  <input
                    type="checkbox"
                    class="cursor-pointer"
                    id="school-class-checkbox-{{schoolClass.id}}"
                    name="class-checkbox"
                    value="copy-class-{{schoolClass.id}}"
                    checked={{includes selectRecords.selectedIds schoolClass.id}}
                    {{on "click" (fn selectRecords.toggle schoolClass.id)}}
                  />
                </label>
              </td>
              <td class="p-3">{{schoolClass.teacherNames}}</td>
              <td class="p-3">{{schoolClass.originalName}}</td>
              <td
                class="p-3
                  {{if
                    (gt schoolClass.studentCount MAX_STUDENTS_PER_CLASS)
                    'bg-watermelony-red-50 text-watermelony-red-350'
                  }}"
              >{{schoolClass.studentCount}}</td>
              <td class="p-3">
                <InputText
                  class="w-full"
                  @outlineForced={{schoolClass.isInvalid}}
                  @themeKey={{if schoolClass.errors ThemeKeys.orange this.themeKey}}
                  disabled={{not (includes selectRecords.selectedIds schoolClass.id)}}
                  value={{schoolClass.name}}
                  {{on "input" (pick "target.value" (fn this.onChangeName schoolClass))}}
                />
              </td>
            </tr>
          {{else if this.hasLoaded}}
            <tr>
              <td class="p-5" {{colspanMax}}>
                {{t "components.copyClasses.table.noResults"}}
              </td>
            </tr>
          {{/each}}
          <RowLoading class="p-5" @show={{@fetchSchoolClasses.isRunning}} @absolute={{this.hasLoaded}} />
        </tbody>
      </table>
    </DataOperationsTable>
  </template>
}
